// @ts-nocheck
import { useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { RosterInputStep } from "./RosterInputStep";
import { RosterReviewStep } from "./RosterReviewStep";
import type {
  IngestionStep,
  ParseResponse,
  ExtractedPlayer,
  CommitPlayer,
} from "@/lib/roster-ingestion/types";
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

interface Props {
  eventId: string;
  teamId: string;
  organizationId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function RosterIngestionDialog({
  eventId,
  teamId,
  organizationId,
  open,
  onOpenChange,
  onSuccess,
}: Props) {
  const { profile } = useAuth();

  const [step, setStep] = useState<IngestionStep>("input");
  const [error, setError] = useState<string | null>(null);
  const [parseResult, setParseResult] = useState<ParseResponse | null>(null);
  const [committing, setCommitting] = useState(false);

  const reset = () => {
    setStep("input");
    setError(null);
    setParseResult(null);
    setCommitting(false);
  };

  const handleOpenChange = (v: boolean) => {
    if (!v) reset();
    onOpenChange(v);
  };

  // ── Call the edge function ──

  const callParseRoster = useCallback(
    async (body: Record<string, any>) => {
      setStep("parsing");
      setError(null);

      try {
        const { data, error: fnError } = await invokeEdgeFunction(
          "parse-roster",
          {
            body: {
              ...body,
              event_id: eventId,
              team_id: teamId,
            },
          }
        );

        if (fnError) {
          throw new Error(fnError.message || "Parse failed");
        }

        const result = data as ParseResponse;

        if (!result?.players || result.players.length === 0) {
          setError("No players could be extracted. Check your input and try again.");
          setStep("input");
          return;
        }

        setParseResult(result);
        setStep("review");
      } catch (err: any) {
        console.error("Parse roster error:", err);
        setError(err.message || "Failed to parse roster");
        setStep("input");
      }
    },
    [eventId, teamId]
  );

  // ── Upload helper ──

  const uploadAndParse = useCallback(
    async (file: File, inputType: "file" | "image") => {
      setStep("parsing");
      setError(null);

      try {
        const userId = profile?.id;
        if (!userId) {
          setError("Not authenticated.");
          setStep("input");
          return;
        }

        const path = `${userId}/${Date.now()}-${file.name}`;
        const { error: uploadError } = await supabase.storage
          .from("roster-uploads")
          .upload(path, file);

        if (uploadError) {
          throw new Error(`Upload failed: ${uploadError.message}`);
        }

        const { data: urlData, error: urlError } = await supabase.storage
          .from("roster-uploads")
          .createSignedUrl(path, 300);

        if (urlError || !urlData?.signedUrl) {
          throw new Error("Failed to create signed URL");
        }

        const body: Record<string, any> = {
          input_type: inputType,
          mime_type: file.type,
        };
        if (inputType === "file") {
          body.file_url = urlData.signedUrl;
        } else {
          body.image_url = urlData.signedUrl;
        }

        await callParseRoster(body);
      } catch (err: any) {
        console.error("Upload error:", err);
        setError(err.message || "Failed to upload file");
        setStep("input");
      }
    },
    [profile?.id, callParseRoster]
  );

  // ── Input handlers ──

  const handleSubmitText = (text: string) => {
    callParseRoster({ input_type: "text", content: text });
  };

  const handleSubmitFile = (file: File) => {
    uploadAndParse(file, "file");
  };

  const handleSubmitImage = (file: File) => {
    uploadAndParse(file, "image");
  };

  // ── Commit ──

  const handleConfirm = async (players: CommitPlayer[]) => {
    setCommitting(true);

    const { data, error: rpcError } = await supabase.rpc(
      "commit_parsed_roster",
      {
        p_event_id: eventId,
        p_team_id: teamId,
        p_players: players,
      }
    );

    if (rpcError) {
      console.error("Commit error:", rpcError);
      toast.error(rpcError.message || "Failed to add players to roster");
      setCommitting(false);
      return;
    }

    const result = data as any;
    const total =
      (result?.inserted_player_count || 0) +
      (result?.matched_player_count || 0);
    const skipped = result?.skipped_duplicate_count || 0;

    let msg = `Added ${total} player${total !== 1 ? "s" : ""} to roster`;
    if (skipped > 0) msg += ` (${skipped} duplicates skipped)`;
    toast.success(msg);

    setCommitting(false);
    handleOpenChange(false);
    onSuccess();
  };

  // ── Step labels ──

  const stepLabels: Record<IngestionStep, string> = {
    input: "Add roster",
    parsing: "Parsing...",
    review: "Review players",
    committing: "Saving...",
    done: "Done",
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{stepLabels[step]}</DialogTitle>
        </DialogHeader>

        {/* ── Input step ── */}
        {step === "input" && (
          <RosterInputStep
            onSubmitText={handleSubmitText}
            onSubmitFile={handleSubmitFile}
            onSubmitImage={handleSubmitImage}
            submitting={false}
            error={error}
          />
        )}

        {/* ── Parsing step ── */}
        {step === "parsing" && (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
            <p className="text-sm text-muted-foreground">
              Parsing roster with AI...
            </p>
            <p className="text-xs text-muted-foreground">
              This usually takes 5-15 seconds
            </p>
          </div>
        )}

        {/* ── Review step ── */}
        {step === "review" && parseResult && (
          <RosterReviewStep
            players={parseResult.players}
            warnings={parseResult.warnings}
            eventId={eventId}
            teamId={teamId}
            onConfirm={handleConfirm}
            onBack={() => {
              setStep("input");
              setError(null);
            }}
            onCancel={() => handleOpenChange(false)}
            committing={committing}
          />
        )}

        {/* ── Committing step ── */}
        {step === "committing" && (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
            <p className="text-sm text-muted-foreground">
              Adding players to roster...
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}